import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import Lenis from "lenis";

// App shells run their own internal scroll containers — keep native scroll there.
const APP_PREFIXES = ["/admin", "/dashboard"];

// Sticky-nav height, so anchored sections don't hide behind the header.
const ANCHOR_OFFSET = -80;

const prefersReduced = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Site-wide smooth scroll. One shared Lenis instance drives the public pages;
 * the logged-in app shells (/admin, /dashboard) stay on native scroll so their
 * internal overflow panes don't fight the page scroll. Honors
 * prefers-reduced-motion.
 *
 * Because Lenis owns the scroll, the browser's native behaviors DON'T work on
 * their own and have to be driven explicitly here:
 *   - in-page anchor links (#id) — a native hash jump does nothing under Lenis;
 *   - scroll restoration on route change — SPA navigations otherwise keep the
 *     previous scroll position, which made footer links like "Blog" look like
 *     they "went nowhere" (you'd land at the bottom of the new page).
 */
export const useSmoothScroll = () => {
  const { pathname, hash } = useLocation();
  const isApp = APP_PREFIXES.some((p) => pathname.startsWith(p));
  const lenisRef = useRef(null);

  // Lenis lifecycle.
  useEffect(() => {
    if (isApp || prefersReduced()) return undefined;

    const html = document.documentElement;
    const prevBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = "auto"; // Lenis and CSS smooth-scroll conflict.

    const lenis = new Lenis({ lerp: 0.1, smoothWheel: true });
    lenisRef.current = lenis;

    let raf = requestAnimationFrame(function loop(t) {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    });

    // Raw `<a href="#id">` clicks change the hash without a route change; React
    // Router won't see them, so we catch the hashchange and drive Lenis to it.
    const onHashChange = () => {
      const target =
        window.location.hash && document.querySelector(window.location.hash);
      if (target) lenis.scrollTo(target, { offset: ANCHOR_OFFSET });
    };
    window.addEventListener("hashchange", onHashChange);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("hashchange", onHashChange);
      lenis.destroy();
      lenisRef.current = null;
      html.style.scrollBehavior = prevBehavior;
    };
  }, [isApp]);

  // On React-Router navigation: jump to the hash target if there is one, else
  // reset to the top. A short delay lets the new route's DOM lay out first.
  useEffect(() => {
    if (isApp || prefersReduced()) return undefined;

    const run = () => {
      const lenis = lenisRef.current;
      if (hash) {
        const target = document.querySelector(hash);
        if (target) {
          if (lenis) lenis.scrollTo(target, { offset: ANCHOR_OFFSET });
          else target.scrollIntoView();
          return;
        }
      }
      if (lenis) lenis.scrollTo(0, { immediate: true });
      else window.scrollTo(0, 0);
    };

    const t = setTimeout(run, 80);
    return () => clearTimeout(t);
  }, [pathname, hash, isApp]);
};

export default useSmoothScroll;
